// Service areas displayed on the public site.

export interface ServiceMetro {
  city: string;
  state: string;
  slug: string;
  blurb: string;
  latitude?: number;
  longitude?: number;
  content?: {
    intro: string;
    economy: string;
    irsContext: string;
    neighborhoods: string[];
  };
}

export const headquarters = {
  city: "Prosper",
  state: "TX",
  region: "Dallas–Fort Worth Metroplex",
};

export const primaryMetros: ServiceMetro[] = [
  {
    city: "Dallas", state: "TX", slug: "dallas-tx",
    blurb: "Local representation for Dallas-area taxpayers and businesses.",
    latitude: 32.7767, longitude: -96.797,
    content: {
      intro: "Dallas is one of the most-audited metros in the IRS Southwest Area, and the Dallas IRS office on Commerce Street handles a high volume of Collection Due Process hearings, examination appeals, and Offer in Compromise reviews each year. We represent Dallas individuals and businesses through every stage of an IRS case — from a first CP14 notice to a contested Tax Court petition.",
      economy: "Dallas's mix of professional services, healthcare, energy, and small-business activity produces a steady volume of payroll-tax cases (941/940), partnership audits, and 1099-K underreporter notices that drive the metro's resolution caseload.",
      irsContext: "Cases originating in Dallas are typically routed through the IRS Service Center in Austin and assigned Revenue Officers from the Dallas Field Collection office. Local Tax Court calendars sit at the Earle Cabell Federal Building.",
      neighborhoods: ["Downtown / Uptown", "Oak Lawn", "Lakewood", "Preston Hollow", "North Dallas", "Bishop Arts / Oak Cliff"],
    },
  },
  {
    city: "Fort Worth", state: "TX", slug: "fort-worth-tx",
    blurb: "IRS resolution and audit defense for the Fort Worth metro.",
    latitude: 32.7555, longitude: -97.3308,
    content: {
      intro: "Fort Worth has a deep small-business and energy-services economy, which generates a steady flow of Trust Fund Recovery Penalty cases, payroll-tax (Form 941) delinquencies, and Schedule C audits. We represent Fort Worth taxpayers in every type of IRS matter.",
      economy: "Manufacturing, oilfield services, healthcare, and a large independent-contractor workforce make payroll-tax and self-employment audits especially common here.",
      irsContext: "Revenue Officer assignments in Fort Worth typically come out of the Tarrant County field office. Collection Due Process hearings are conducted remotely or by phone unless the taxpayer requests in-person.",
      neighborhoods: ["Sundance Square", "TCU / West 7th", "Near Southside", "Arlington Heights"],
    },
  },
  {
    city: "Plano", state: "TX", slug: "plano-tx",
    blurb: "CPA-led tax resolution serving Plano and Collin County.",
    latitude: 33.0198, longitude: -96.6989,
    content: {
      intro: "Plano's concentration of technology workers, executives with equity compensation, and high-income filers produces a distinctive resolution caseload — RSU and ISO underreporter notices, complex state-vs-federal allocations for remote workers, and high-balance Offer in Compromise cases tied to one-time stock sales.",
      economy: "Corporate headquarters, technology consulting, and a deep base of professional services define Plano's tax-resolution profile.",
      irsContext: "Collin County cases share Revenue Officer coverage with Dallas and McKinney; appeals are typically handled remotely.",
      neighborhoods: ["West Plano", "Legacy / Shops at Legacy", "Willow Bend", "Downtown Plano"],
    },
  },
  {
    city: "Frisco", state: "TX", slug: "frisco-tx",
    blurb: "Tax resolution help for Frisco-area taxpayers and small businesses.",
    latitude: 33.1507, longitude: -96.8236,
    content: {
      intro: "Frisco has been one of the fastest-growing metros in Texas, with a heavy concentration of small businesses, real estate investors, and dual-state remote workers. We represent Frisco residents in every type of IRS matter, including the increasingly common 1099-K and short-term-rental audits.",
      economy: "Real estate, small business, professional services, and dual-state tech employment drive the local resolution caseload.",
      irsContext: "Frisco cases route through the Plano / Collin County field office. We coordinate Texas Comptroller sales-tax issues for business clients when they overlap with federal exposure.",
      neighborhoods: ["Frisco Square", "Stonebriar", "Newman Village", "Phillips Creek Ranch"],
    },
  },
  {
    city: "McKinney", state: "TX", slug: "mckinney-tx",
    blurb: "Local IRS representation for McKinney residents and businesses.",
    latitude: 33.1972, longitude: -96.6398,
    content: {
      intro: "McKinney's mix of long-established small businesses and rapid suburban growth produces a steady resolution caseload. We routinely handle local payroll-tax cases, contractor 1099-NEC audits, and high-balance installment agreements for McKinney clients.",
      economy: "Construction, real estate development, professional services, and small-business retail define the local tax-resolution profile.",
      irsContext: "McKinney cases share field-collection coverage with Plano. Appeals and CDP hearings are typically handled remotely.",
      neighborhoods: ["Historic Downtown McKinney", "Stonebridge Ranch", "Craig Ranch", "Adriatica"],
    },
  },
  {
    city: "Houston", state: "TX", slug: "houston-tx",
    blurb: "Tax debt relief and audit defense across the Houston metro.",
    latitude: 29.7604, longitude: -95.3698,
    content: {
      intro: "Houston's energy, healthcare, and shipping economy produces one of the largest IRS resolution caseloads in the country. We represent Houston taxpayers in everything from straightforward installment agreements to multi-year offshore disclosure cases and energy-sector partnership audits.",
      economy: "Energy, petrochemicals, healthcare, and a large international business community generate complex resolution matters — including foreign-bank-account (FBAR) and partnership-level audits.",
      irsContext: "Houston has its own IRS Field Collection office and Tax Court calendar. Revenue Officer assignments are common at higher balances given the concentration of large local businesses.",
      neighborhoods: ["Downtown", "The Heights", "River Oaks", "Galleria / Uptown", "Memorial", "Sugar Land"],
    },
  },
  {
    city: "Austin", state: "TX", slug: "austin-tx",
    blurb: "CPA-led IRS resolution for Austin individuals and businesses.",
    latitude: 30.2672, longitude: -97.7431,
    content: {
      intro: "Austin's startup and technology economy produces a high volume of equity-compensation issues — RSU vesting, ISO exercises, 83(b) elections, and one-time liquidity events that create unexpected balances. We represent Austin technology workers and founders in resolving these exposures cleanly.",
      economy: "Technology, startups, music, hospitality, and short-term rentals dominate the local resolution caseload.",
      irsContext: "Austin houses the IRS Service Center that processes paper returns and many resolution requests nationally — but cases originating in Austin are still handled by Texas field offices for Revenue Officer purposes.",
      neighborhoods: ["Downtown", "South Congress", "East Austin", "Westlake", "Cedar Park"],
    },
  },
  {
    city: "San Antonio", state: "TX", slug: "san-antonio-tx",
    blurb: "Comprehensive tax resolution for the San Antonio area.",
    latitude: 29.4241, longitude: -98.4936,
    content: {
      intro: "San Antonio's military, healthcare, and small-business economy produces a distinctive caseload — military spouse residency issues, Schedule C audits for medical professionals, and a steady volume of payroll-tax cases for the city's deep small-business base.",
      economy: "Military, healthcare, tourism, and a substantial small-business sector drive resolution work in San Antonio.",
      irsContext: "San Antonio has its own IRS Field Collection office. Tax Court calendars rotate through the federal courthouse downtown.",
      neighborhoods: ["Downtown", "Alamo Heights", "Stone Oak", "Northwest Side"],
    },
  },
];

export const nationalCoverage = {
  headline: "Federal tax representation, nationwide",
  body: "CPAs are federally authorized to represent taxpayers before the IRS in all 50 states. We work with clients across the country — phone, video, and secure document exchange.",
};

export function getMetro(slug: string): ServiceMetro | undefined {
  return primaryMetros.find((m) => m.slug === slug);
}
